import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, User, LogOut, LayoutDashboard } from 'lucide-react';
import { useState } from 'react';

export const Navbar = () => {
  const { user, userData, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { name: 'Ana Səhifə', path: '/' },
    { name: 'Oyunlar', path: '/games' },
    { name: 'Filiallar', path: '/branches' },
  ];

  return (
    <nav className="fixed w-full z-50 glass-dark" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 animate-fade-in" data-testid="logo-link">
            <img src="/logo.png" alt="Phobia Games" className="w-16 sm:w-20 h-auto" />
            <span className="text-sm sm:text-lg font-black text-white font-montserrat">
              PHOBIA GAMES
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center" style={{ gap: '2.5rem' }}>
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-gray-300 hover:text-red-400 transition-all duration-300 font-semibold text-base whitespace-nowrap"
                data-testid={`nav-${link.path.replace('/', '') || 'home'}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-red-400 font-semibold"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-5 h-5 mr-2" />
                    {userData?.name || user.email}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="glass-dark border-gray-700 text-white">
                  <DropdownMenuItem 
                    onClick={() => navigate('/dashboard')}
                    className="hover:bg-red-500/20 cursor-pointer font-medium"
                    data-testid="my-reservations-link"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Rezervasiyalarım
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="bg-gray-700" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        className="hover:bg-red-500/20 cursor-pointer text-red-400 font-medium"
                        data-testid="admin-panel-link"
                      >
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="bg-gray-700" />
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="hover:bg-red-500/20 cursor-pointer font-medium"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Çıxış
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-gray-300 hover:text-red-400 font-semibold"
                    data-testid="login-btn"
                  >
                    Giriş
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="btn-primary text-white px-6 py-2 rounded-lg font-bold"
                    data-testid="register-btn"
                  >
                    Qeydiyyat
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white hover:text-red-400 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-8 h-8" /> : <Menu className="w-8 h-8" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden glass-dark border-t border-gray-800">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="block px-3 py-3 text-gray-300 hover:text-red-400 transition-colors font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gray-800 flex flex-col gap-2 px-3">
                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full text-gray-300 justify-start font-semibold">
                        Rezervasiyalarım
                      </Button>
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className="w-full text-red-400 justify-start font-semibold">
                          Admin Panel
                        </Button>
                      </Link>
                    )}
                    <Button 
                      variant="ghost" 
                      className="w-full text-gray-300 justify-start font-semibold"
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    >
                      Çıxış
                    </Button>
                  </>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full text-gray-300 font-semibold">Giriş</Button>
                    </Link>
                    <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full btn-primary text-white font-bold rounded-lg">Qeydiyyat</Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
